import React from 'react';
import { Card, Button, Result, Typography, Space, Divider } from 'antd';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import InvitationAcceptForm from './InvitationAcceptForm';
import type { InvitationDetails } from './InvitationAcceptForm';
import { getErrorMessage } from '../../utils/errorMessage';
import { SERVICE_TITLE } from '@shared/config/service-config';

const InvitationAcceptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { Title, Text, Paragraph } = Typography;

  const verifyInvitationMutation = trpc.user.verifyInvitation.useMutation();
  const acceptInvitationMutation = trpc.user.acceptInvitation.useMutation();

  if (!token) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 16px',
      }}>
        <Card style={{ width: '100%', maxWidth: 520, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <Result
            status="error"
            title="無効な招待リンク"
            subTitle="招待メールの正しいリンクをクリックしてください。"
            extra={
              <Button type="primary" onClick={() => navigate('/login')}>
                ログインページに戻る
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const handleVerify = async (inputToken: string): Promise<InvitationDetails> => {
    try {
      const result = await verifyInvitationMutation.mutateAsync({ token: inputToken });
      return {
        inviterName: result.inviterName ?? result.inviterEmail ?? '管理者',
        inviteeEmail: result.inviteeEmail,
        companyName: result.companyName ?? '-',
        role: result.role,
        expiresAt: result.expiresAt,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, '招待情報の取得に失敗しました。'));
    }
  };

  const handleAccept = async (input: { token: string; password: string }) => {
    try {
      await acceptInvitationMutation.mutateAsync(input);
    } catch (error) {
      throw new Error(getErrorMessage(error, '招待の受諾に失敗しました。'));
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px 16px',
    }}>
      <Card style={{ width: '100%', maxWidth: 640, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Title level={2} style={{ marginBottom: 4, color: '#1890ff' }}>
                {SERVICE_TITLE}
              </Title>
            </Link>
            <Text type="secondary">招待を受諾してアカウントを設定</Text>
          </div>
          <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 0 }}>
            パスワードを設定し、利用規約に同意するとアカウントが有効になります。
          </Paragraph>

          <InvitationAcceptForm
            token={token}
            onVerifyToken={handleVerify}
            onAcceptInvitation={handleAccept}
            onSuccessRedirect={() => navigate('/login?message=invitation_accepted')}
            termsUrl="/legal/terms-privacy"
          />

          <Divider style={{ margin: '8px 0' }} />
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">何か問題がありますか？</Text>{' '}
            <Button type="link" onClick={() => navigate('/login')}>
              ログインページへ戻る
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default InvitationAcceptPage;
